import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";

const CATEGORIES = ["All", "Hoodies", "Tees", "Outerwear", "Pants", "Accessories"];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (query) list = list.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, category, query, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14" data-testid="shop-page">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36] mb-3">The Store</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight uppercase leading-[1.05] mb-10">
          Every piece. Every size.<br />While it lasts.
        </h1>
      </Reveal>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors ${
                category === c
                  ? "bg-[#E05A36] text-[#0C0A09] border-[#E05A36] font-bold"
                  : "border-[#2C2623] text-[#B8AEA5] hover:border-[#E05A36] hover:text-[#E05A36]"
              }`}
              data-testid={`filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-3 lg:ml-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7067]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pieces"
              className="bg-[#171412] border border-[#2C2623] focus:border-[#E05A36] outline-none pl-9 pr-4 py-2 text-sm w-48 transition-colors"
              data-testid="shop-search-input"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#171412] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
            data-testid="shop-sort-select"
          >
            <option value="featured">Featured</option>
            <option value="low">Price: Low → High</option>
            <option value="high">Price: High → Low</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-[#7A7067] font-mono text-sm uppercase tracking-widest py-20 text-center" data-testid="shop-loading">Loading the drop...</p>
      ) : filtered.length === 0 ? (
        <p className="text-[#7A7067] font-mono text-sm uppercase tracking-widest py-20 text-center" data-testid="shop-empty">Nothing here. Try another filter.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="product-grid">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.05, 0.4)}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;
